import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import './WorkOrderDetailsHeader.css';
import EstimateBudgetInvoiceButtons from 'components/admin/work-orders/details/ClientLinkButtonsContainer';
import chatBubbles from 'images/chat-bubbles.png';
import chatBubblesIndicator from 'images/chat-bubbles-indicator.png';
import ClientStreamModal from 'components/admin/work-orders/details/ClientStreamModal';
import {
  WorkOrderStatusEnum,
  WorkOrderStatusKeys,
} from 'services/utils/work-order-status-enum';
import Date from 'components/shared/text/Date';
import Number from 'components/shared/text/Number';
import { PermissionsUtil } from 'services/utils/permissions-util';
import { WorkOrder } from 'domain/work-order';
import DetailsHeader from 'components/shared/details/DetailsHeader';
import { getCurrentWorkOrder } from 'selectors/admin/work-orders/work-orders';
import { TYPES, usersUtil } from 'services/utils/users-util';
import { getCurrentUser } from 'selectors/authentication';
import {
  getBudget,
  getQuotes,
} from 'selectors/admin/work-orders/details/details';
import { setShowClientStreamModalRequest } from 'actions/admin/work-orders/details/details';
import { Contractor } from 'domain/contractor';
import { timeSelector } from 'components/admin/work-orders/details/time/store/selectors';
import { vendorInvoicesSelector } from 'components/admin/work-orders/details/vendor-invoices/store/selectors';
import { receiptsSelector } from 'components/admin/work-orders/details/receipts/store/selectors';
import { getChangeOrderTasks } from 'components/admin/work-orders/details/change-order-tasks/store/selectors';

class WorkOrderDetailsHeader extends Component {
  static propTypes = {
    budget: PropTypes.shape({}),
    currentUser: PropTypes.shape({}).isRequired,
    currentWorkOrder: PropTypes.shape({}).isRequired,
    materials: PropTypes.array.isRequired,
    quotes: PropTypes.array.isRequired,
    time: PropTypes.array.isRequired,
    vendorInvoices: PropTypes.array.isRequired,
    onChatClick: PropTypes.func.isRequired,
  };

  static defaultProps = {
    budget: null,
  };

  constructor(props) {
    super(props);
    const { currentUser } = this.props;
    this.userIsAdmin =
      usersUtil.isAdmin(currentUser) ||
      usersUtil.hasType(currentUser, TYPES.GENERAL_CONTRACTOR);
    this.userIsContractor = usersUtil.hasType(currentUser, TYPES.CONTRACTOR);
    this.userIsClient = usersUtil.hasType(currentUser, TYPES.CLIENT);
  }

  handleOpenClientStream = (event) => {
    event.preventDefault();
    this.props.onChatClick(this.props.currentWorkOrder.id);
  };

  completeHeaderForAdmin = (defaultHeaders, totalCost) => {
    const { budget, currentUser, currentWorkOrder } = this.props;
    const result = defaultHeaders.slice();
    result[0].push({
      title: 'TOTAL COST',
      value:
        totalCost !== null ? <Number value={totalCost} format="$0,0" /> : 'TBD',
    });
    if (
      PermissionsUtil.adminUserCanSeeMoney(currentUser) ||
      usersUtil.hasType(currentUser, TYPES.GENERAL_CONTRACTOR)
    ) {
      result[0].push({
        title: 'VARIANCE',
        value: WorkOrder.getVariance(
          { ...currentWorkOrder, ...{ budget: budget } },
          'N/A'
        ),
      });
    }
    return result;
  };

  completeHeaderForClient = (defaultHeaders, totalCost) => {
    const result = defaultHeaders.slice();
    result[0].push({
      title: 'TOTAL COST',
      value:
        totalCost !== null ? <Number value={totalCost} format="$0,0" /> : 'TBD',
    });
    return result;
  };

  completeHeaderForContractor = (defaultHeaders) => {
    const {
      currentWorkOrder,
      quotes,
      materials,
      time,
      vendorInvoices,
      changeOrderTasks,
    } = this.props;
    const result = defaultHeaders.slice();
    if (
      WorkOrder.isStatusFulFilled(
        currentWorkOrder,
        WorkOrderStatusKeys.IN_PROGRESS
      )
    ) {
      // Create new line of header
      result.push([
        {
          title: 'QUOTES COST',
          value: (
            <Number
              value={Contractor.getTotalCost(quotes, changeOrderTasks, true)}
              format="$0,0"
            />
          ),
        },
        {
          title: 'PROFIT',
          value: (
            <Number
              value={Contractor.getNetCost(
                quotes,
                changeOrderTasks,
                materials,
                time,
                vendorInvoices
              )}
              format="$0,0"
            />
          ),
        },
        {
          title: 'VARIANCE PERCENTAGE',
          value: (
            <Number
              value={Contractor.getVariancePercentage(
                quotes,
                changeOrderTasks,
                materials,
                time,
                vendorInvoices
              )}
              format="0%"
            />
          ),
        },
      ]);
    } else {
      result[0].push({
        title: 'TOTAL COST',
        value: <Number value={Contractor.getTotalCost(quotes)} format="$0,0" />,
      });
    }
    return result;
  };

  getRightHeaders = () => {
    const { currentWorkOrder } = this.props;
    const startDate = WorkOrder.getStartDate(currentWorkOrder);
    const completionDate = WorkOrder.getCompletionDate(currentWorkOrder);
    const totalCost = WorkOrder.getTotalCost(currentWorkOrder);

    const defaultHeaders = [
      [
        {
          title: 'STATUS',
          value: WorkOrderStatusEnum.create(currentWorkOrder.status).label,
        },
        {
          title: 'START',
          value:
            startDate !== null ? (
              <Date value={startDate} format={'M/D/YYYY'} />
            ) : (
              'TBD'
            ),
        },
        {
          title: 'COMPLETION',
          value:
            completionDate !== null ? (
              <Date value={completionDate} format={'M/D/YYYY'} />
            ) : (
              'TBD'
            ),
        },
      ],
    ];

    // Complete headers for different users
    let headers;
    if (this.userIsAdmin) {
      headers = this.completeHeaderForAdmin(defaultHeaders, totalCost);
    } else if (this.userIsClient) {
      headers = this.completeHeaderForClient(defaultHeaders, totalCost);
    } else {
      headers = this.completeHeaderForContractor(defaultHeaders);
    }

    return headers;
  };

  render() {
    const { currentWorkOrder } = this.props;
    return (
      <div className="work-order-details-header">
        <DetailsHeader
          leftHeader={{
            title: 'CLIENT',
            value: currentWorkOrder.client.company_name,
          }}
          leftHeaderExtraComponents={
            <React.Fragment>
              <EstimateBudgetInvoiceButtons />

              {(this.userIsAdmin || this.userIsClient) && (
                <div className="client-stream-container">
                  <img
                    className="chat-bubbles"
                    src={chatBubbles}
                    alt="chat-bubbles"
                    onClick={this.handleOpenClientStream}
                  />
                  {currentWorkOrder.unread_notes && (
                    <img
                      className="chat-bubbles-indicator"
                      src={chatBubblesIndicator}
                      alt="chat-bubbles-indicator"
                    />
                  )}
                  <ClientStreamModal />
                </div>
              )}
            </React.Fragment>
          }
          rightHeaders={this.getRightHeaders()}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    budget: getBudget(state),
    currentUser: getCurrentUser(state),
    currentWorkOrder: getCurrentWorkOrder(state),
    materials: receiptsSelector(state),
    quotes: getQuotes(state),
    time: timeSelector(state),
    vendorInvoices: vendorInvoicesSelector(state),
    changeOrderTasks: getChangeOrderTasks(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  onChatClick: (workOrderId) => {
    dispatch(setShowClientStreamModalRequest(workOrderId));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(WorkOrderDetailsHeader);



// WEBPACK FOOTER //
// ./src/components/admin/work-orders/details/WorkOrderDetailsHeader.js